/* eslint-disable react/destructuring-assignment */
/* eslint-disable prefer-destructuring */
/* eslint-disable max-len */
import React, { useCallback, useEffect, useRef, useState, useContext } from 'react';
import { Button } from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { messages as platformMessages } from 'gw-platform-translations';
import renderNode from './ElementMapping';
import SignDocumentComponent from '../../SignDocumentComponent/SignDocumentComponent';
import styles from './AdacConfigurableForm.module.scss';
import messages from './AdacConfigurableForm.messages';
import { parseJSONStringToObject, validateDateField, validateTextField, getLabelForElement } from './ElementMappingUtil';
import { _ } from 'core-js';

const AdacConfigurableForm = ({
    sendButtonClick,
    htmlTemplate,
    isSendingDocument,
    handleError,
    validationOutcome,
    setValidationOutcome
}) => {
    const [isDocumentSigning, setIsDocumentSigning] = useState(false);
    const [isDocumentSigned, setIsDocumentSigned] = useState(false);
    const [formElements, setFormElements] = useState([]);
    const [formData, setFormData] = useState({});
    const [validHtmlTemplate, setValidHtmlTemplate] = useState(true);
    const [validationErrors, setValidationErrors] = useState({});
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [formInputs, setFormInputs] = useState([]);

    const canvasRef = useRef({});
    const viewModelService = useContext(ViewModelServiceContext);

    const translator = useTranslator();

    const isFormValid = Object.values(validationErrors).length === 0;

    const addAttributesToNodes = (node) => {
        if (node.nodeType === 1) {
            // Add a unique ID
            node.setAttribute('uniqueId', Math.random().toString(36).substr(2, 9));

            // Add a label to node if one exists
            if (['input', 'textarea', 'select'].includes(node.tagName.toLowerCase())) {
                const label = getLabelForElement(node);
                if (label) {
                    node.setAttribute('label', label);
                }
            }
        }

        Array.from(node.children).forEach(addAttributesToNodes);
    };

    useEffect(() => {
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlTemplate, 'text/html');
            const form = doc.getElementById('form');

            if (!form) {
                throw new Error('Form not found in the template');
            }

            addAttributesToNodes(form);

            setFormInputs(Array.from(form.querySelectorAll('input, textarea, select'))); 

            setFormElements(Array.from(form.children));
        } catch (error) {
            handleError(messages.errorReadingTemplate, true);
            setValidHtmlTemplate(false);
        }
    }, [htmlTemplate, handleError]);

    const applyAnnotationValidations = useCallback((currentFormData, childElement, validationMessages) => {
        const validationAttributes = childElement.getAttribute('data-bind-validation');
        if (!_.isEmpty(validationAttributes)) {
            const currentValue = currentFormData[childElement.name];

            // Sample tag value: data-bind-validation="@Required(),@Size(0,20)"
            // We split by "," this is followed by "@" to not break parameters. Here we would have "[@Required(), @Size(0,20)]"
            const annotations = validationAttributes.split(new RegExp(',(?=@)'));
            annotations.forEach((entry) => {
                const openingBracketIndex = entry.indexOf('(');
                const annotationName = entry.substring(1, openingBracketIndex);
                const annotationParametersString = entry.substring(openingBracketIndex + 1, entry.length - 1);

                const parameters = {};
                if (!_.isEmpty(annotationParametersString)) {
                    switch (annotationName) {
                        case 'Pattern':
                            // Pattern needs separate treating in order to not break the regex.
                            const patternAnnotationParameters = annotationParametersString.split("','");
                            parameters.firstParameter = patternAnnotationParameters[0].substr(1);
                            parameters.secondParameter = patternAnnotationParameters[1].substr(0, patternAnnotationParameters[1].length - 1);
                            break;
                        default:
                            const defaultAnnotationParameters = annotationParametersString.split(',');
                            // Currently all our annotations have 0, 1 or 2 parameters.
                            // If it has elements it's means there is at least 1 param.

                            parameters.firstParameter = defaultAnnotationParameters[0];
                            if (defaultAnnotationParameters.length === 2) {
                                parameters.secondParameter = defaultAnnotationParameters[1];
                            }
                    }
                }

                // Initialization of the DTO is needed to use OOTB Expressions validation mechanism
                // This allows us to have consistant validation on frontend and backend.
                // For more details take a look into AnnotationDTO in CC.
                const nodeVM = viewModelService.create(
                    {
                        data: currentValue,
                        ...parameters
                    },
                    'cc',
                    `adac.edge.capabilities.claim.documentSign.annotationdtos.${annotationName}DTO`
                );

                const isValid = nodeVM.data.aspects.valid;

                if (isValid === false) {
                    validationMessages.push(nodeVM.data.aspects.validationMessage);
                }
            });
        }
    }, [viewModelService]);

    const applyBackendValidations = useCallback((childElement, validationMessages, backendValidation) => {
        // Backend validations are more of an precaution from manual request manipulation.
        // Theoretically should be aligned with Frontend validations as both rely on data-bind-validation tag.
        // Nevertheless, they will be shown around the fields
        if (!_.isEmpty(backendValidation)
                && backendValidation.validationErrors[childElement.name] !== undefined
                && !_.isEmpty(backendValidation.validationErrors[childElement.name])) {
            backendValidation.validationErrors[childElement.name].forEach((entry) => validationMessages.push(entry));
        }
    }, []);

    const applyDataBindTypeValidations = useCallback((currentValue, childElement, validationMessages) => {
        switch (childElement.getAttribute('data-bind-type')) {
            case 'integer':
                if (currentValue !== undefined && !_.isEmpty(currentValue)) {
                    const numberValue = Number(currentValue);
                    if (Number.isNaN(numberValue)) {
                        validationMessages.push(translator(platformMessages.notANumber));
                    } else {
                        const isInteger = Number.isInteger(numberValue);
                        if (!isInteger) {
                            validationMessages.push(translator(platformMessages.notAnInteger));
                        }
                    }
                }
                break;
            default:
                // Nothing needs to be done here.
        }
    }, [translator]);

    const validateForm = useCallback((currentFormData, backendValidation) => {
        let formValid = true;
        setValidationErrors({});

        formInputs.forEach((childElement) => {
            if (childElement.required && !currentFormData[childElement.name]) {
                const errorMessage = translator(messages.requiredFieldValidationMessage);
                setValidationErrors((prevErrors) => ({
                    ...prevErrors,
                    [childElement.name]: errorMessage,
                }));
                formValid = false;
            }

            const validationMessages = [];
            if (childElement.tagName.toLowerCase() === 'input'
                && childElement.type === 'date'
                && currentFormData[childElement.name]) {
                const dateValue = currentFormData[childElement.name];
                const validationRules = parseJSONStringToObject(childElement.getAttribute('validateDate') || '{}');
                const validationMessage = validateDateField(
                    dateValue,
                    validationRules,
                    childElement.required,
                    translator
                );

                if (validationMessage !== null) {
                    validationMessages.push(validationMessage);
                }
            }

            if (childElement.tagName.toLowerCase() === 'input'
                && childElement.type === 'text'
                && currentFormData[childElement.name]) {
                const currentValue = currentFormData[childElement.name];
                const validationRules = parseJSONStringToObject(childElement.getAttribute('validation') || '{}');
                const validationMessage = validateTextField(
                    currentValue,
                    validationRules,
                    childElement.required,
                    translator
                );

                if (validationMessage !== null) {
                    validationMessages.push(validationMessage);
                }
            }

            applyDataBindTypeValidations(currentFormData[childElement.name], childElement, validationMessages);
            applyAnnotationValidations(currentFormData, childElement, validationMessages);
            applyBackendValidations(childElement, validationMessages, backendValidation);

            if (!_.isEmpty(validationMessages)) {
                setValidationErrors((prevErrors) => ({
                    ...prevErrors,
                    [childElement.name]: validationMessages
                }));
                formValid = false;
            }
        });
        return formValid;
    }, [applyAnnotationValidations, applyBackendValidations, applyDataBindTypeValidations, formInputs, translator]);

    useEffect(() => {
        if (validationOutcome.initialValidation === true) {
            validateForm(formData, validationOutcome);
        }
    }, [validateForm, validationOutcome]);

    const handleChange = useCallback((name, value) => {
        const backendValidation = _.clone(validationOutcome);
        if (!_.isEmpty(backendValidation) && backendValidation.validationErrors[name] !== undefined) {
            backendValidation.validationErrors[name] = [];
            backendValidation.initialValidation = false;
            setValidationOutcome(backendValidation);
        }

        setFormData((prevState) => {
            const updatedFormState = {
                ...prevState,
                [name]: value
            };

            if (hasSubmitted) {
                validateForm(updatedFormState, backendValidation);
            }

            return updatedFormState;
        });
    }, [setFormData, validationOutcome, setValidationOutcome, hasSubmitted, validateForm]);

    const renderForm = useCallback(() => {
        try {
            return formElements.map((element) => (
                <div key={element}>
                    {Array.from(element.children)
                        .map((child) => renderNode(
                            child,
                            formData,
                            handleChange,
                            validationErrors,
                            !isFormValid
                        ))}
                </div>
            ));
        } catch (error) {
            handleError(messages.errorReadingTemplate, true);
            return null;
        }
    }, [formElements, formData, handleChange, handleError, validationErrors, isFormValid]);

    const handleFormSubmit = (event) => {
        event.preventDefault();

        const formValid = validateForm(formData, validationOutcome);
        setHasSubmitted(true);

        if (formValid) {
            setIsDocumentSigning(true);
        }
    };

    const handleSubmitDocument = (event) => {
        event.preventDefault();

        const formValid = validateForm(formData, validationOutcome);
        setHasSubmitted(true);

        if (formValid) {
            // Format any Date objects in formData to 'dd.MM.yyyy' format to
            // ensure consistent date representation before initiating the document signing process.
            const formattedFormData = { ...formData };
            Object.keys(formattedFormData).forEach((field) => {
                if (formattedFormData[field] instanceof Date) {
                    formattedFormData[field] = formattedFormData[field].toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                    });
                }
            });
            setIsDocumentSigning(true);

            sendButtonClick(canvasRef, formattedFormData);
        }
    };

    return (
        <div>
            {renderForm()}
            {!isFormValid && (
                <div className={styles.error}>
                    <span>{translator(messages.errorMandatoryFields)}</span>
                </div>
            )}
            {!isDocumentSigning && validHtmlTemplate && (
                <div className={styles.buttonContainer}>
                    <Button onClick={handleFormSubmit}>
                        {translator(messages.signButton)}
                    </Button>
                </div>
            )}
            {isDocumentSigning && (
                <SignDocumentComponent
                    canvasRef={canvasRef}
                    isDocumentSigned={isDocumentSigned}
                    setIsDocumentSigned={setIsDocumentSigned}
                    isSendingDocument={isSendingDocument}
                    submitDocumentSign={handleSubmitDocument}
                />
            )}
        </div>
    );
};

export default AdacConfigurableForm;
